import { useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateRelationship, useUpdateRelationship } from '@/hooks/useRelationships';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { PersonAutocomplete } from './PersonAutocomplete';
import type { Relationship } from '@/api/relationships.api';

// ─── Zod-Schema ────────────────────────────────────────────────────────────

const relationshipFormSchema = z.object({
  partnerId: z.string().min(1, 'Partner ist erforderlich.'),
  type: z.enum(['married', 'partnership', 'divorced', 'separated']),
  startDateText: z.string().max(50).optional().or(z.literal('')),
  endDateText: z.string().max(50).optional().or(z.literal('')),
  startPlace: z.string().max(200).optional().or(z.literal('')),
  notes: z.string().max(2000).optional().or(z.literal('')),
});

type RelationshipFormValues = z.infer<typeof relationshipFormSchema>;

// ─── Labels ────────────────────────────────────────────────────────────────

const TYPE_OPTIONS: Array<{ value: RelationshipFormValues['type']; label: string }> = [
  { value: 'married', label: 'Verheiratet' },
  { value: 'partnership', label: 'Partnerschaft' },
  { value: 'divorced', label: 'Geschieden' },
  { value: 'separated', label: 'Getrennt' },
];

// ─── Props ─────────────────────────────────────────────────────────────────

interface RelationshipFormProps {
  /** ID der Person, für die die Beziehung angelegt wird */
  personId: string;
  /** Schließt das Modal */
  onClose: () => void;
  /** Bestehende Beziehung zum Bearbeiten (Edit-Modus) */
  existingRelationship?: Relationship;
}

// ─── RelationshipForm ──────────────────────────────────────────────────────

export function RelationshipForm({ personId, onClose, existingRelationship }: RelationshipFormProps) {
  const createRelationship = useCreateRelationship();
  const updateRelationship = useUpdateRelationship();
  const confirm = useConfirm();
  const isEditMode = !!existingRelationship;

  // Im Edit-Modus: Partner-ID ermitteln
  const existingPartnerId = existingRelationship
    ? existingRelationship.person1Id === personId
      ? existingRelationship.person2Id
      : existingRelationship.person1Id
    : '';

  // Datum als Text formatieren (aus ISO-String)
  const formatDateForForm = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
    } catch {
      return '';
    }
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<RelationshipFormValues>({
    resolver: zodResolver(relationshipFormSchema),
    defaultValues: {
      partnerId: existingPartnerId,
      type: (existingRelationship?.type as RelationshipFormValues['type']) ?? 'married',
      startDateText: formatDateForForm(existingRelationship?.startDate ?? null),
      endDateText: formatDateForForm(existingRelationship?.endDate ?? null),
      startPlace: existingRelationship?.startPlace ?? '',
      notes: existingRelationship?.notes ?? '',
    },
  });

  // ─── Schließen mit Dirty-Guard ────────────────────────────────────────

  const requestClose = useCallback(async () => {
    if (isSubmitting) return;
    if (isDirty) {
      const ok = await confirm({
        title: 'Eingaben verwerfen?',
        message: 'Nicht gespeicherte Änderungen gehen verloren.',
        confirmLabel: 'Verwerfen',
        cancelLabel: 'Weiter bearbeiten',
        variant: 'default',
      });
      if (!ok) return;
    }
    onClose();
  }, [confirm, isDirty, isSubmitting, onClose]);

  // ─── ESC schließt Modal (mit Dirty-Guard) ─────────────────────────────

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') requestClose();
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [requestClose]);

  // ─── Absenden ─────────────────────────────────────────────────────────

  const onSubmit = async (values: RelationshipFormValues) => {
    try {
      if (isEditMode) {
        await updateRelationship.mutateAsync({
          id: existingRelationship!.id,
          dto: {
            type: values.type,
            // Geleerte Felder als null senden, damit sie in der DB geleert
            // werden (undefined ließe sie unverändert)
            start_date_text: values.startDateText || null,
            end_date_text: values.endDateText || null,
            start_place: values.startPlace || null,
            notes: values.notes || null,
          },
        });
      } else {
        await createRelationship.mutateAsync({
          person1_id: personId,
          person2_id: values.partnerId,
          type: values.type,
          start_date_text: values.startDateText || undefined,
          end_date_text: values.endDateText || undefined,
          start_place: values.startPlace || undefined,
          notes: values.notes || undefined,
        });
      }
      onClose();
    } catch {
      // Fehler werden durch den Hook behandelt
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={requestClose}>
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEditMode ? 'Partnerschaft bearbeiten' : 'Partnerschaft hinzufügen'}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Schließen"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Formular */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-5 py-4">
          {/* Partner-Autocomplete (im Edit-Modus deaktiviert) */}
          {!isEditMode && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Partner <span className="text-red-500">*</span>
              </label>
              <Controller
                name="partnerId"
                control={control}
                render={({ field }) => (
                  <PersonAutocomplete
                    value={field.value || null}
                    onChange={(id) => field.onChange(id ?? '')}
                    placeholder="Partner suchen..."
                    excludeIds={[personId]}
                    aria-label="Partner"
                  />
                )}
              />
              {errors.partnerId && (
                <p className="mt-1 text-xs text-red-500">{errors.partnerId.message}</p>
              )}
            </div>
          )}

          {/* Typ */}
          <div>
            <label htmlFor="rf-type" className="mb-1 block text-sm font-medium text-slate-700">
              Art der Beziehung
            </label>
            <select
              id="rf-type"
              {...register('type')}
              className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                         focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Datum & Ort */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="rf-startDate" className="mb-1 block text-sm font-medium text-slate-700">
                Beginn
              </label>
              <input
                id="rf-startDate"
                type="text"
                {...register('startDateText')}
                placeholder="z.B. 14.06.2010"
                className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                           placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div>
              <label htmlFor="rf-endDate" className="mb-1 block text-sm font-medium text-slate-700">
                Ende
              </label>
              <input
                id="rf-endDate"
                type="text"
                {...register('endDateText')}
                placeholder="z.B. 2022"
                className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                           placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>

          {/* Ort */}
          <div>
            <label htmlFor="rf-startPlace" className="mb-1 block text-sm font-medium text-slate-700">
              Ort
            </label>
            <input
              id="rf-startPlace"
              type="text"
              {...register('startPlace')}
              placeholder="z.B. Standesamt München"
              className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                         placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* Notizen */}
          <div>
            <label htmlFor="rf-notes" className="mb-1 block text-sm font-medium text-slate-700">
              Notizen
            </label>
            <textarea
              id="rf-notes"
              {...register('notes')}
              rows={2}
              className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                         placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors
                         hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                         disabled:opacity-50"
            >
              {isSubmitting ? 'Speichern...' : isEditMode ? 'Änderungen speichern' : 'Partnerschaft speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
